'use client';
import {useCallback, useMemo, useSyncExternalStore} from 'react';
import {GenerationManager} from './GenerationManager';
import type {GenerationScreenState} from './GenerationStreamState';
import {trpc} from '@features/utils/trpc';
import {
  clearStreamingGenTypeAtom,
  setStreamingGenTypeAtom,
} from './streamingGenTypeAtom';
import {useSetAtom} from 'jotai';
import amplitudeService from '@features/analytics/amplitudeService';
import {
  sessionLimitAtom,
  type SessionLimitInfo,
} from '@features/subscription/sessionLimitAtom';
import {invalidateFlowActiveStructures} from '@features/flow/utils/invalidateScreenCaches';

const globalManager = new GenerationManager();

function returnUndefined() {
  return undefined;
}

function useGenerationStore<T>(
  flowId: string,
  screenId: string,
  getSnapshot: (screen: GenerationScreenState) => T,
) {
  useClientProvideGeneration();
  const screen = globalManager.getScreen(flowId, screenId);
  const getSnapshotFn = useCallback(
    () => getSnapshot(screen),
    [getSnapshot, screen],
  );

  return useSyncExternalStore(screen.store.subscribe, getSnapshotFn);
}

/**
 * Grabs a screen's store, but doesn't create on / trigger generation is doesn't exist yet
 * @param screenId
 * @param getSnapshot
 * @returns
 */
function useWeakGenerationStore<T>(
  screenId: string | undefined | null,
  getSnapshot: (screen?: GenerationScreenState) => T,
) {
  const subscribe =
    getScreen(screenId)?.store.subscribe ??
    globalManager.screenListStore.subscribe;
  const getSnapshotFn = useCallback(
    () => getSnapshot(getScreen(screenId)),
    [getSnapshot, screenId],
  );

  return useSyncExternalStore(subscribe, getSnapshotFn, returnUndefined);
}

export function useScreenGenerationUpdates(screenId: string) {
  const subscribe =
    getScreen(screenId)?.generationUpdatesStore.subscribe ??
    globalManager.screenListStore.subscribe;

  const getSnapshotFn = useCallback(
    () => getGenerationStatusUpdates(getScreen(screenId)),
    [screenId],
  );

  return useSyncExternalStore(subscribe, getSnapshotFn, returnUndefined);
}

function getGenerationStatusUpdates(s: GenerationScreenState | undefined) {
  return s?.generationUpdates;
}

function getScreen(screenId: string | undefined | null) {
  return screenId ? globalManager.screens.get(screenId) : undefined;
}

export function useGenerationContents(flowId: string, screenId: string) {
  return useGenerationStore(flowId, screenId, generationContents);
}

function generationContents(s: GenerationScreenState) {
  return s.generationPromise ? s.currentStruct : null;
}

export function useGeneratingScreenname(screenId: string) {
  return useWeakGenerationStore(screenId, screenName);
}

function screenName(s: GenerationScreenState | undefined) {
  return s?.generationPromise && s.currentStruct && s.newName;
}

export function useIsScreenGenerating(screenId: string | undefined | null) {
  return useWeakGenerationStore(screenId, isGenerating);
}

function isGenerating(screen?: GenerationScreenState) {
  return screen?.isGenerating;
}
export function useIsAnyScreenGenerating() {
  return useSyncExternalStore(
    globalManager.allScreenStatuses.subscribe,
    isAnyGenerating,
  );
}

// Just in case this is later not using the sync store etc.
export function useStopGeneration() {
  return stopGeneration;
}

function stopGeneration(screenId: string) {
  getScreen(screenId)?.stopGeneration();
}

function isAnyGenerating() {
  return (
    globalManager.screens.values().find((a) => a.isGenerating)?.isGenerating ??
    false
  );
}

export function useClarifyingQuestions(screenId: string | undefined | null) {
  return useWeakGenerationStore(screenId, getClarifyingQuestions);
}

function getClarifyingQuestions(screen?: GenerationScreenState) {
  return screen?.clarifyingQuestions ?? null;
}

/** Triggers a re-query of the generation of a screen if none is in progress */
export function useTriggerGeneration() {
  useClientProvideGeneration();

  return triggerGeneration;
}
const emptyScreenIDs = '[]';

export function useGeneratingScreens(flowId: string) {
  const screenIDs = useSyncExternalStore(
    globalManager.allScreenStatuses.subscribe,
    () => getGeneratingScreens(flowId),
    () => emptyScreenIDs,
  );

  return useMemo(() => JSON.parse(screenIDs) as string[], [screenIDs]);
}

function getGeneratingScreens(flowId: string) {
  return JSON.stringify(
    [
      ...globalManager.screens
        .values()
        .filter((a) => a.flowId === flowId && a.generationPromise)
        .map((a) => a.screenId),
    ].toSorted(),
  );
}

function triggerGeneration(flowId: string, screenId: string) {
  globalManager.getScreen(flowId, screenId).triggerDownload('sendmessage');
}

// Must cover the longest window: session resets are ≤5h out, day resets ≤24h.
const SESSION_RESUME_MAX_MS = 24 * 3600_000;
const sessionResumeTimers = new Map<string, ReturnType<typeof setTimeout>>();

function scheduleSessionResume(
  info: SessionLimitInfo,
  resume: () => void,
): void {
  const ms = info.resetsAt.getTime() - Date.now();
  if (ms <= 0 || ms > SESSION_RESUME_MAX_MS) return;
  const existing = sessionResumeTimers.get(info.screenId);
  if (existing) clearTimeout(existing);
  sessionResumeTimers.set(
    info.screenId,
    setTimeout(() => {
      // eslint-disable-next-line drizzle/enforce-delete-with-where
      sessionResumeTimers.delete(info.screenId);
      resume();
    }, ms),
  );
}

function useClientProvideGeneration() {
  const utils = trpc.useUtils();
  const clearStreamingGenType = useSetAtom(clearStreamingGenTypeAtom);
  const setStreamingGenType = useSetAtom(setStreamingGenTypeAtom);
  const setSessionLimit = useSetAtom(sessionLimitAtom);
  const abortGeneration = trpc.generation.abortGeneration.useMutation();

  // eslint-disable-next-line react-hooks/immutability
  globalManager.onSessionLimit = (info) => {
    // The authoritative "[Session Limit] reached" is emitted server-side (fires
    // in shadow mode too); the client just surfaces the banner.
    setSessionLimit(info);
    // Auto-resume the queued message when the window resets (while the tab
    // stays open). A reload/next send also resumes it.
    scheduleSessionResume(info, () => {
      setSessionLimit(null);
      // Sequential: several screens' timers fire at the same reset instant.
      globalManager.resumeSequentially(info.flowId, info.screenId);
    });
  };

  // We don't need to useCallback this - no performance difference
  // eslint-disable-next-line react-hooks/immutability
  globalManager.invalidate = async (flowId, screenId) => {
    amplitudeService.logEvent('[Chat] send message ai response', {
      flowId,
      screenId,
    });
    await Promise.all([
      utils.screens.getScreensWithStructure.invalidate(),
      utils.screens.getStructureList.invalidate({screenId}),
      utils.screens.getScreen.invalidate({screenId}),
      utils.subscription.getSubscriptionInfo.invalidate(),
      utils.flow.getFlow.invalidate({flowId}),
      utils.screens.getFlowScreens.invalidate({flowId}),
      utils.screens.recentUserMessages.invalidate({screenId}),
      utils.themes.getVisibleThemes.invalidate(),
      // Chat screen: forced refetch works for any id format (legacy UUID or
      // component) and even when the query is inactive.
      utils.screens.getActiveStructure.refetch({screenId}),
      // A components turn can touch screens besides the chat one — refresh
      // the rest of this flow's screens (scoped; repairs inactive entries).
      invalidateFlowActiveStructures(utils, flowId),
      utils.flow.getUndoStatus.invalidate({flowId}),
      utils.themes.getComponentFlowTokens.invalidate({flowId}),
    ]);

    // Clear stale streaming genType now that structure is saved with correct genType.
    // Without this, switching versions via History would use the stale streaming
    // genType instead of the structure's genType for frame dimensions.
    clearStreamingGenType(screenId);
  };

  // eslint-disable-next-line react-hooks/immutability
  globalManager.fetchScreenContents = async (screenId: string) => {
    return (
      (await utils.screens.getActiveStructure.ensureData({screenId}))
        ?.htmlCss ?? ''
    );
  };
  // eslint-disable-next-line react-hooks/immutability
  globalManager.setScreenType = (screenId, genType) => {
    if (genType) {
      setStreamingGenType({screenId, genType});
    } else {
      clearStreamingGenType(screenId);
    }
  };

  // eslint-disable-next-line react-hooks/immutability
  globalManager.sendAbortGeneration = (abortToken) => {
    abortGeneration.mutate({abortToken});
  };

  // eslint-disable-next-line react-hooks/immutability
  globalManager.getInitialScreenIds = (flowId) => {
    const screens = utils.screens.getFlowScreens.getData({flowId});
    return new Set(screens?.map((s) => s.id) ?? []);
  };

  // eslint-disable-next-line react-hooks/immutability
  globalManager.logScreenEvents = (
    flowId,
    initialScreenIds,
    touchedScreenIds,
  ) => {
    const flow = utils.flow.getFlow.getData({flowId});
    if (flow?.genType !== 'components') return;
    for (const id of touchedScreenIds) {
      amplitudeService.logEvent(
        initialScreenIds.has(id)
          ? '[AI] screen edited'
          : '[AI] screen generated',
        {
          flowId,
          screenId: id,
          genType: 'components',
          model: flow.model,
        },
      );
    }
  };
}
